import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { HoursInDaySelectedPipe } from '../../../../../shared/pipes/hours-in-day-selected.pipe';
import { TabDirective } from '../../../panel/tab-view/directives/tab.directive';
import { TabViewComponent } from '../../../panel/tab-view/tab-view.component';
import { DayLabels } from '../contract/day-labels.contract';
import { TimeUnitValue } from '../contract/time-unit-value.contract';
import { TimefenceWeekData } from '../contract/timefence-week-data.contract';
import { DayOfWeek } from '../enums/day-of-week.enum';
import { TimefenceDayComponent } from '../timefence-day/timefence-day.component';

@Component({
  selector: 'osp-timefence-week',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TabViewComponent,
    TabDirective,
    TimefenceDayComponent,
    HoursInDaySelectedPipe,
    TranslateModule,
  ],
  templateUrl: './timefence-week.component.html',
  styleUrl: './timefence-week.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TimefenceWeekComponent),
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class TimefenceWeekComponent implements ControlValueAccessor {
  private destroyRef = inject(DestroyRef);

  dayLabels = input<DayLabels>();

  activeIndex = signal(0);
  formControl = computed(() => this.form.controls[this.days[this.activeIndex()]]);
  private onTouched: () => void = () => {};

  readonly days = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
  ];

  form = new FormGroup({
    [DayOfWeek.MONDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.TUESDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.WEDNESDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.THURSDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.FRIDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.SATURDAY]: new FormControl<TimeUnitValue[]>([]),
    [DayOfWeek.SUNDAY]: new FormControl<TimeUnitValue[]>([]),
  });

  formChange = toSignal(this.form.valueChanges);

  writeValue(obj: TimefenceWeekData): void {
    this.form.patchValue(obj);
  }

  registerOnChange(fn: (value: TimefenceWeekData) => void): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => fn(value));
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected readonly DayOfWeek = DayOfWeek;
}
